import { isFunction } from 'lodash';
import { useCallback } from 'react';

import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, TranslatableText, useTranslate, WithFieldController } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { translationKeys } from './translations';

export type PatientExternalIdentifierType =
   | typeof Pesel.CLASS
   | typeof Passport.CLASS
   | typeof NationalIdentificationNumber.CLASS;

const PATIENT_EXTERNAL_IDENTIFIER_TYPES: PatientExternalIdentifierType[] = [
   Pesel.CLASS,
   Passport.CLASS,
   NationalIdentificationNumber.CLASS,
];

export type PatientExternalIdentifierTypeVisitor = <ACCEPT>(
   visitor:
      | ((type: PatientExternalIdentifierType) => ACCEPT)
      | Record<`visit${PatientExternalIdentifierType}`, () => ACCEPT>
) => ACCEPT;

export const createPatientExternalIdentifierTypeVisitor = (
   patientExternalIdentifierType: PatientExternalIdentifierType
): PatientExternalIdentifierTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(patientExternalIdentifierType);
   }

   switch (patientExternalIdentifierType) {
      case 'Pesel':
         return visitor.visitPesel();
      case 'Passport':
         return visitor.visitPassport();
      case 'NationalIdentificationNumber':
         return visitor.visitNationalIdentificationNumber();
      default:
         return assertUnreachable(patientExternalIdentifierType);
   }
};

const useTriggerLabelExtractor = (): ((type?: PatientExternalIdentifierType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(translationKeys.patientExternalIdentifierType.selectItemName[type]) : '';
      },
      [translate]
   );
};

export type OnPatientExternalIdentifierTypeSelect = (type: PatientExternalIdentifierType | null) => void;

type PatientExternalIdentifierTypeFieldProps = WithFieldController<unknown> & {
   readonly onSelect: OnPatientExternalIdentifierTypeSelect;
   readonly label?: TranslatableText;
};

export const PatientExternalIdentifierTypeField = (props: PatientExternalIdentifierTypeFieldProps): JSX.Element => {
   return (
      <Select<PatientExternalIdentifierType>
         testId={'patientExternalIdentifierType'}
         label={props.label || translationKeys.patientExternalIdentifierType.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.controller}
         defaultValue={'Pesel'}
         onSelect={props.onSelect}
      >
         <Select.List<PatientExternalIdentifierType> items={PATIENT_EXTERNAL_IDENTIFIER_TYPES}>
            {(identifierType) => (
               <Select.Item
                  testId={identifierType}
                  item={identifierType}
                  label={translationKeys.patientExternalIdentifierType.selectItemName[identifierType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
